import { type ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';

import { FormFieldCombobox } from '~/components/FormField';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { useLabelInfiniteList } from '~settings/api/queries/labelInfiniteList';
import type { LabelListQueryVariables } from '~settings/api/queries/labelList';
import { useLabelList } from '~settings/api/queries/labelList';
import { useDropdownPageSize } from '~settings/hooks/useDropdownPageSize';
import { toDropdownList } from '~settings/utils/transformers';

interface SelectLabelsProps
  extends Omit<
      ComponentProps<typeof FormFieldCombobox>,
      | 'clientSearch'
      | 'displaySelectedOptions'
      | 'label'
      | 'options'
      | 'onSearchChange'
      | 'search'
    >,
    Partial<Pick<ComponentProps<typeof FormFieldCombobox>, 'label'>> {
  labelType?: 'all' | 'parent_label' | 'sub_label';
  type?: 'view' | 'create' | 'edit';
}

export function SelectLabels({
  labelType = 'all',
  multiselect = true,
  name,
  type = 'view',
  ...props
}: SelectLabelsProps) {
  const { t } = useTranslation('settings', { keyPrefix: 'dropdowns.label' });
  const [dropdownPaginationEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerSettingsDropdownPagination,
  ]);

  const [currentSearch, setCurrentSearch] = useState<string>();
  const [search, setSearch] = useState(currentSearch);

  useDebounce(
    () => {
      setSearch(currentSearch);
    },
    250,
    [currentSearch],
  );

  const extraParams: Partial<LabelListQueryVariables> = {
    take: useDropdownPageSize(),
  };

  if (type === 'edit') {
    extraParams.canEditUsers = true;
  } else if (type === 'create') {
    extraParams.canCreateUsers = true;
  }

  if (labelType !== 'all') {
    extraParams.labelType = labelType;
  }

  if (dropdownPaginationEnabled && search) {
    extraParams.search = search;
  }

  const { isLoading, labelList } = useLabelList(
    {
      skip: 0,
      ...extraParams,
    },
    {
      enabled: !dropdownPaginationEnabled,
    },
  );

  const {
    isLoading: isLoadingInfinite,
    labelInfiniteList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLabelInfiniteList(extraParams, {
    enabled: dropdownPaginationEnabled,
  });

  const onLoadMore = async () => {
    if (hasNextPage && dropdownPaginationEnabled && !isFetchingNextPage) {
      await fetchNextPage();
    }
  };

  const labels =
    labelInfiniteList?.pages.flatMap((page) => page.data) ??
    labelList?.data ??
    [];

  const count = multiselect ? 2 : 1;

  return (
    <FormFieldCombobox
      label={t('title', { count })}
      placeholder={t('placeholder', { count })}
      searchPlaceholder={t('searchPlaceholder', { count })}
      {...props}
      clientSearch={!dropdownPaginationEnabled}
      displaySelectedOptions={multiselect}
      multiselect={multiselect}
      name={name}
      onSearchChange={setCurrentSearch}
      options={toDropdownList(labels)}
      readOnly={isLoading || isLoadingInfinite || props.readOnly}
      search={currentSearch}
      noOptionsText={t('noOptions')}
      onLoadMore={onLoadMore}
      extendOptions
    />
  );
}
SelectLabels.displayName = 'SelectLabels';
